import type {
  ContactCreateRequest,
  Customer,
  CustomerContactUpdateRequest,
  CustomerCreateRequest,
  CustomerDuplicateCheckResponse,
  CustomerPage,
  CustomerUpdateRequest,
} from '../api/active/generated/customer';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';

export class CustomerApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code?: string,
    message = '客户请求失败',
    readonly fieldErrors: unknown[] = [],
  ) {
    super(message);
  }
}
type Versioned = { customer: Customer; etag: string };
type CustomerUpdateBody = Omit<
  CustomerUpdateRequest,
  'ownerUserIds' | 'primaryOwnerUserId' | 'departmentId'
> &
  Partial<
    Pick<
      CustomerUpdateRequest,
      'ownerUserIds' | 'primaryOwnerUserId' | 'departmentId'
    >
  > & {
    customFields?: Array<{
      definitionId: string;
      definitionVersion: number;
      value: unknown;
    }>;
  };
async function parseError(result: Response): Promise<CustomerApiError> {
  let problem: {
    code?: string;
    errorCode?: string;
    title?: string;
    detail?: string;
    fieldErrors?: unknown[];
  } = {};
  try {
    problem = (await result.json()) as typeof problem;
  } catch {
    /* non-JSON failure */
  }
  return new CustomerApiError(
    result.status,
    problem.code ?? problem.errorCode,
    problem.detail ??
      problem.title ??
      `后端请求失败（HTTP ${result.status}）。`,
    problem.fieldErrors ?? [],
  );
}
async function response<T>(
  transport: ActiveTransport,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const result = await transport.requestResponse(path, init);
  if (!result.ok) throw await parseError(result);
  return (await result.json()) as T;
}
export function createIdempotencyKey(): string {
  return randomUuid();
}
export function createCustomerApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  return {
    list: (
      page: number,
      pageSize: number,
      search: string,
      signal?: AbortSignal,
    ) =>
      response<CustomerPage>(
        transport,
        `/customers?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`,
        { signal },
      ),
    get: async (id: string, signal?: AbortSignal): Promise<Versioned> => {
      const result = await transport.requestResponse(
        `/customers/${encodeURIComponent(id)}`,
        { signal },
      );
      if (!result.ok) throw await parseError(result);
      return {
        customer: (await result.json()) as Customer,
        etag: result.headers.get('ETag') ?? '',
      };
    },
    duplicate: (body: { name?: string; phone?: string }) =>
      response<CustomerDuplicateCheckResponse>(
        transport,
        '/customers:check-duplicates',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mutationHeaders,
          },
          body: JSON.stringify(body),
        },
      ),
    create: (
      body: CustomerCreateRequest,
      idempotencyKey = createIdempotencyKey(),
    ) =>
      write(
        transport,
        '/customers',
        'POST',
        body,
        undefined,
        idempotencyKey,
        csrfToken,
      ),
    update: (
      id: string,
      etag: string,
      body: CustomerUpdateBody,
      idempotencyKey = createIdempotencyKey(),
    ) =>
      write(
        transport,
        `/customers/${encodeURIComponent(id)}`,
        'PATCH',
        body,
        etag,
        idempotencyKey,
        csrfToken,
      ),
    addContact: (
      id: string,
      etag: string,
      body: ContactCreateRequest,
      idempotencyKey = createIdempotencyKey(),
    ) =>
      write(
        transport,
        `/customers/${encodeURIComponent(id)}/contacts`,
        'POST',
        body,
        etag,
        idempotencyKey,
        csrfToken,
      ),
    updateContact: (
      id: string,
      contactId: string,
      etag: string,
      body: CustomerContactUpdateRequest,
      idempotencyKey = createIdempotencyKey(),
    ) =>
      write(
        transport,
        `/customers/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`,
        'PATCH',
        body,
        etag,
        idempotencyKey,
        csrfToken,
      ),
    setPrimary: (
      id: string,
      contactId: string,
      etag: string,
      idempotencyKey = createIdempotencyKey(),
    ) =>
      write(
        transport,
        `/customers/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}:set-primary`,
        'POST',
        undefined,
        etag,
        idempotencyKey,
        csrfToken,
      ),
  };
}
async function write(
  transport: ActiveTransport,
  path: string,
  method: string,
  body: unknown,
  etag: string | undefined,
  idempotencyKey: string,
  csrfToken: string | null,
): Promise<Versioned> {
  const result = await transport.requestResponse(path, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      'Idempotency-Key': idempotencyKey,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...(etag ? { 'If-Match': etag } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!result.ok) throw await parseError(result);
  return {
    customer: (await result.json()) as Customer,
    etag: result.headers.get('ETag') ?? '',
  };
}
